import React, {
  useState, useCallback, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Select, { components } from 'react-select';
import { FixedSizeList } from 'react-window';
import IcomoonReact from 'icomoon-react';
import iconSet from '../../../shared/images/teambuildr-selection.json';

const OPTION_HEIGHT = 36;
const MAX_VISIBLE = 5;

// Search mode container: sits inline inside the row's matched cell.
// No padding/border — lets the Select fill the cell edge-to-edge.
const SearchContainer = styled('div')`
  position: relative;
  width: 100%;
`;

const FooterOption = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 14px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #5b86e5;
  cursor: pointer;
  border-top: 1px solid #F0F0F0;
  transition: background 0.12s ease;
  letter-spacing: 0.1px;

  &:hover {
    background: rgba(91, 134, 229, 0.06);
  }
`;

const getOptionBackground = (state) => {
  if (state.isSelected) return 'rgba(91, 134, 229, 0.08)';
  if (state.isFocused) return '#F6F6FA';
  return 'transparent';
};

const selectStyles = {
  // The control fills MatchedCell (which already paints the 1px #444 active
  // border + 4px radius), so we strip the Select's own chrome to avoid the
  // doubled-border look. Transparent bg, no border, no focus glow — the
  // cell is the visual container.
  control: () => ({
    background: 'transparent',
    border: 'none',
    borderRadius: 0,
    minHeight: '32px',
    height: '32px',
    boxShadow: 'none',
    fontFamily: '\'Nunito Sans\', sans-serif',
    fontSize: '16px',
    cursor: 'text',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0',
    height: '32px',
  }),
  menu: (base) => ({
    ...base,
    background: 'white',
    border: '1px solid #E8E8EE',
    borderRadius: '8px',
    boxShadow: '0 10px 32px rgba(20, 20, 40, 0.14), 0 2px 6px rgba(20, 20, 40, 0.05)',
    overflow: 'hidden',
    zIndex: 9999,
    marginTop: 4,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menuList: (base) => ({
    ...base,
    padding: 0,
    maxHeight: 'none',
  }),
  option: (base, state) => ({
    ...base,
    fontFamily: '\'Nunito Sans\', sans-serif',
    fontSize: '13px',
    cursor: 'pointer',
    borderRadius: '4px',
    padding: '9px 12px',
    margin: '2px 4px',
    width: 'auto',
    background: getOptionBackground(state),
    color: state.isSelected ? '#5b86e5' : '#2b2b3a',
    fontWeight: state.isSelected ? 700 : 500,
    '&:active': {
      background: '#EFEFF5',
    },
  }),
  singleValue: (base) => ({
    ...base,
    color: '#2b2b3a',
    fontFamily: '\'Nunito Sans\', sans-serif',
    fontWeight: 600,
  }),
  placeholder: (base) => ({
    ...base,
    color: '#9E9E9E',
    fontFamily: '\'Nunito Sans\', sans-serif',
    fontSize: '16px',
  }),
  input: (base) => ({
    ...base,
    color: '#444444',
    fontFamily: '\'Nunito Sans\', sans-serif',
    fontSize: '16px',
    margin: 0,
    padding: 0,
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: () => ({ display: 'none' }),
  clearIndicator: (base) => ({
    ...base,
    color: '#C9C9D4',
    padding: '4px',
    '&:hover': { color: '#6F6F82' },
  }),
  noOptionsMessage: (base) => ({
    ...base,
    color: '#BFBFC8',
    fontFamily: '\'Nunito Sans\', sans-serif',
    fontSize: '13px',
  }),
};

const VirtualMenuListWithFooter = (props) => {
  const { children, selectProps } = props;
  const rows = Array.isArray(children) ? children : [];
  const onCreateClick = selectProps?.onCreateClick;

  const footer = (
    <FooterOption
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onCreateClick) onCreateClick();
      }}
    >
      <IcomoonReact iconSet={iconSet} size={11} icon='plus' color='#5b86e5' />
      Create new exercise
    </FooterOption>
  );

  if (rows.length === 0) {
    return (
      <div>
        <components.NoOptionsMessage {...props}>
          No exercises found
        </components.NoOptionsMessage>
        {footer}
      </div>
    );
  }

  const height = Math.min(rows.length, MAX_VISIBLE) * OPTION_HEIGHT;
  return (
    <div>
      <FixedSizeList
        height={height}
        itemCount={rows.length}
        itemSize={OPTION_HEIGHT}
        width='100%'
      >
        {({ index, style }) => <div style={style}>{rows[index]}</div>}
      </FixedSizeList>
      {footer}
    </div>
  );
};

VirtualMenuListWithFooter.propTypes = {
  children: PropTypes.node,
  selectProps: PropTypes.shape({
    onCreateClick: PropTypes.func,
  }),
};

VirtualMenuListWithFooter.defaultProps = {
  children: null,
  selectProps: {},
};

const InlineExerciseEditor = ({
  exercise,
  exerciseOptions,
  isLoadingOptions,
  onSwap,
  onCreateClick,
  onCancel,
}) => {
  const [inputValue, setInputValue] = useState('');
  const selectRef = useRef(null);

  const filteredOptions = useMemo(() => {
    if (!exerciseOptions) return [];
    if (!inputValue) return exerciseOptions;
    const words = inputValue.toLowerCase().trim().split(/\s+/);
    return exerciseOptions.filter((opt) => {
      const name = opt.label.toLowerCase();
      return words.every((w) => name.includes(w));
    });
  }, [exerciseOptions, inputValue]);

  const handleSelect = useCallback((option) => {
    if (!option) return;
    onSwap(exercise.id, option);
  }, [exercise.id, onSwap]);

  const handleCreate = useCallback(() => {
    if (onCreateClick) onCreateClick(exercise.id, exercise.rawName);
  }, [exercise.id, exercise.rawName, onCreateClick]);

  const handleSelectKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    }
  }, [onCancel]);

  return (
    <SearchContainer>
      <Select
        ref={selectRef}
        classNamePrefix='ai-import-select'
        options={filteredOptions}
        onChange={handleSelect}
        onInputChange={(val) => setInputValue(val)}
        inputValue={inputValue}
        menuIsOpen
        autoFocus
        isSearchable
        isClearable={false}
        isLoading={isLoadingOptions}
        placeholder='Search exercises…'
        noOptionsMessage={() => 'No exercises found'}
        styles={selectStyles}
        components={{ MenuList: VirtualMenuListWithFooter }}
        onKeyDown={handleSelectKeyDown}
        filterOption={() => true}
        onCreateClick={handleCreate}
        // Absolute positioning inside the body-portal so the menu scrolls
        // with the page in lockstep with its trigger. menuPosition='fixed'
        // locks the menu to the viewport, which detaches it from the
        // trigger as soon as the user scrolls the wizard.
        menuPosition='absolute'
        menuPlacement='auto'
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        // The wizard's PageWrapper owns the scroll, not the document, so an
        // absolute-positioned portaled menu still drifts off its trigger
        // when the coach scrolls the page. Close on any scroll that isn't
        // happening *inside* the menu itself (so the menu's own scroll
        // doesn't close it).
        closeMenuOnScroll={(e) => {
          const t = e.target;
          if (t instanceof Element && t.closest('.ai-import-select__menu')) {
            return false;
          }
          return true;
        }}
      />
    </SearchContainer>
  );
};

InlineExerciseEditor.propTypes = {
  exercise: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    rawName: PropTypes.string.isRequired,
    name: PropTypes.string,
    isNew: PropTypes.bool,
  }).isRequired,
  exerciseOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.number,
    label: PropTypes.string,
  })),
  isLoadingOptions: PropTypes.bool,
  onSwap: PropTypes.func.isRequired,
  onCreateClick: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

InlineExerciseEditor.defaultProps = {
  exerciseOptions: [],
  isLoadingOptions: false,
};

export default InlineExerciseEditor;
